// Stock helpers — single source of truth for reading stock from a live
// product payload returned by GET /api/products. All storefront surfaces
// (PDP, catalog card, cart) go through these helpers so the display and
// quantity limits stay consistent with what the admin actually saved.
//
// Contract:
//   stock === undefined | null  →  unmanaged (no cap, generic "IN STOCK")
//   stock === 0                 →  out of stock (show notify-me)
//   stock  > 0                  →  cap quantity to this number; if ≤ threshold,
//                                  show "ONLY N LEFT" to signal urgency without
//                                  broadcasting raw counts when stock is healthy.

export const LOW_STOCK_THRESHOLD = 10;

export function getVariantStock(variant) {
  const s = variant?.stock;
  return typeof s === "number" ? s : null;
}

// Stock for the specific SKU the user has selected. Variant products key
// on pack_size (which also carries the mg value for Raw Vials).
export function getEffectiveStock(product, variantPackSize = null) {
  if (!product) return null;
  if (variantPackSize != null && Array.isArray(product.variants)) {
    const v = product.variants.find(
      (x) => Number(x.pack_size) === Number(variantPackSize)
    );
    return getVariantStock(v);
  }
  if (Array.isArray(product.variants) && product.variants.length > 0) {
    // No specific variant chosen — take the max across variants so the
    // card doesn't misleadingly declare OOS when a different pack size
    // is still available.
    const stocks = product.variants
      .map((v) => v.stock)
      .filter((s) => typeof s === "number");
    if (stocks.length === 0) return null;
    return Math.max(...stocks);
  }
  return typeof product.stock === "number" ? product.stock : null;
}

// True only when every managed SKU is exhausted (all variants at 0, or a
// non-variant product with stock explicitly at 0). Products where stock
// is unmanaged (undefined) are never treated as OOS.
export function isProductFullyOOS(product) {
  if (!product) return false;
  if (Array.isArray(product.variants) && product.variants.length > 0) {
    const managed = product.variants.filter((v) => typeof v.stock === "number");
    if (managed.length === 0) return false; // stock unmanaged across all variants
    return managed.length === product.variants.length && managed.every((v) => v.stock === 0);
  }
  return product.stock === 0;
}

export function stockLabel(stock, threshold = LOW_STOCK_THRESHOLD) {
  if (stock === null || stock === undefined) return { text: "IN STOCK", tone: "ok" };
  if (stock === 0) return { text: "OUT OF STOCK", tone: "oos" };
  if (stock <= threshold) return { text: `ONLY ${stock} LEFT`, tone: "low" };
  return { text: "IN STOCK", tone: "ok" };
}
